import React, {
  useMemo, useCallback, useEffect, useState, useRef, Fragment, ReactElement, RefObject,
} from 'react';
import Table from '../../../../components/table/table.component';
import TableHead from '../../../../components/table-head/table-head.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import TableCellShort from '../../../../components/table-cell-short/table-cell-short.component';
import Image from '../../../../components/image/image.component';
import Widget from '../../../../components/widget/widget.component';
import Button from '../../../../components/button/button.component';
import List from '../../../../components/list/list.component';
import ListItem from '../../../../components/list-item/list-item.component';
import ListItemText from '../../../../components/list-item-text/list-item-text.component';
import ListItemImage from '../../../../components/list-item-image/list-item-image.component';
import ContentListContainer from '../../../../components/content-list-container/content-list-container.component';
import NoContentBlock from '../../../../components/no-content-block/no-content-block.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import FormFieldMultiSelect from '../../../../components/form-field-multi-select/form-field-multi-select.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import FormatService from '../../../../services/format-service';
import ScreenService from '../../../../services/screen-service';
import useScreenSmall from '../../../../hooks/use-screen-small';
import { PAGE_LIST_DEFAULT } from '../../../../constants/system/pages';
import PersonDefaultImage from '../../../../static/images/person-default-image.png';

const ContactWidgetStaff: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Staff';
  const WIDGET_NAME: string = 'widget-contact-staff';
  const WIDGET_HEIGHT: number = 305;

  const {
    isLoading,
    isEditMode,
    formPrefix,
    contactData,
    companyContactPersonList,
    relatedContactList,
    relatedContactsTotal,
    onContactRowClick,
    onEditModeButtonClick,
    onCreateButtonClick,
    onRelatedContactsChange,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onLoadBatchData,
  } = props;

  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);
  const itemListRef: RefObject<HTMLDivElement> | null = useRef(null);

  const [page, setPage]: [number, Function] = useState(PAGE_LIST_DEFAULT);
  const [selectedRelatedContacts, setSelectedRelatedContacts]: [AssetType[] | null, Function] = useState(null);

  const contactId: number | null = (contactData && contactData.id) || null;
  const commonRelatedContactsTotal: number = (relatedContactList && relatedContactList.length) || 0;

  const isSmallScreen: boolean = useScreenSmall();
  const isEmpty: boolean = !contactData;

  const menuItemCompanyContactPersonList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: companyContactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedRelatedContacts,
  }), [companyContactPersonList, selectedRelatedContacts]);

  const tableRelatedContactListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'photo',
      title: 'Photo',
      component: (contactItem: Contact): ReactElement => (
        <TableCellShort isTextCenter>
          <Image
            isCentered
            text={contactItem.fullName}
            link={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
          />
        </TableCellShort>
      ),
    },
    {
      id: 2,
      name: 'fullName',
      title: 'Full Name',
      component: (contactItem: Contact): ReactElement => (
        <TableCell isHeader>
          {contactItem.fullName}
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'title',
      title: 'Title',
      component: (contactItem: Contact): ReactElement => (
        <TableCell>
          {contactItem.title}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'address',
      title: 'Address',
      component: (contactItem: Contact): ReactElement => (
        <TableCell>
          {
            contactItem.address && contactItem.streetAddress && (
              <FilteredLink isExternal link={contactItem.address.url}>
                {
                  FormatService.formatStringAddress(
                    contactItem.streetAddress,
                    contactItem.location,
                    contactItem.postalCode,
                  )
                }
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
  ], []);

  const relatedContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onRelatedContactsChange) {
      onRelatedContactsChange(contactValues);
    }

    setSelectedRelatedContacts(contactValues);
  }, []);

  const getOptions: Function = useCallback((pageOption?: number): RequestOptions => {
    let options: RequestOptions = {};

    options = contactId ? { ...options, contactId } : options;
    options = pageOption ? { ...options, page: pageOption } : options;

    return options;
  }, [contactId]);

  const loadBatchData: Function = useCallback((): void => {
    if (onLoadBatchData) {
      onLoadBatchData(getOptions(page + PAGE_LIST_DEFAULT));
    }

    setPage(page + PAGE_LIST_DEFAULT);
  }, [page, contactId]);

  const onScroll: MouseScrollHandlerFunction = useCallback((): void => {
    if (containerRef && containerRef.current) {
      ScreenService.infiniteScroll(
        containerRef.current.scrollTop,
        containerRef.current.clientHeight,
        itemListRef && itemListRef.current ? itemListRef.current.clientHeight : 0,
        (): void => {
          if (relatedContactsTotal > commonRelatedContactsTotal && !isLoading) {
            loadBatchData();
          }
        },
        isSmallScreen,
      );
    }
  }, [
    containerRef, itemListRef, relatedContactsTotal, page, contactId, commonRelatedContactsTotal, isLoading,
    isSmallScreen,
  ]);

  useEffect((): void => {
    if (contactData && !selectedRelatedContacts) {
      setSelectedRelatedContacts(relatedContactList || []);
    }
  }, [contactData, isEditMode]);

  return (
    <Widget
      isFullWidth
      withEditMode
      isEmpty={isEmpty}
      isFullHeight={isEditMode}
      isStaticHeight={!isEditMode}
      isEditMode={isEditMode}
      widgetName={WIDGET_NAME}
      defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
      onEditModeButtonClick={onEditModeButtonClick}
    >
      {
        isEditMode ? (
          <FormFieldMultiSelect
            withImage
            withSearch
            isDisabled={companyContactPersonList && !companyContactPersonList.length}
            nameKey="fullName"
            name={`${formPrefix}.relatedContactIds`}
            defaultImage={PersonDefaultImage}
            optionList={companyContactPersonList}
            onValuesChange={relatedContactsChange}
            onSearch={onContactPersonSearch}
            onClose={onContactPersonSearchClose}
          >
            {menuItemCompanyContactPersonList}
          </FormFieldMultiSelect>
        ) : (
          <Fragment>
            {
              relatedContactList && relatedContactList.length ? (
                <ContentListContainer
                  ref={containerRef}
                  onScroll={onScroll}
                  maxHeight={WIDGET_HEIGHT}
                >
                  <div ref={itemListRef}>
                    {
                      !isSmallScreen ? (
                        <Table withStickyHeader>
                          <TableHead isSubHeader>
                            <TableRow>
                              {
                                tableRelatedContactListFields.map((tableField: TableField): ReactElement => (
                                  <TableCell key={tableField.id}>
                                    {tableField.title}
                                  </TableCell>
                                ))
                              }
                            </TableRow>
                          </TableHead>
                          <TableBody isPlain withZebraRows>
                            {
                              relatedContactList.map((contactItem: Contact): ReactElement => (
                                <TableRow
                                  isButton
                                  key={contactItem.id}
                                  onClick={(): void => {
                                    if (onContactRowClick) {
                                      onContactRowClick(contactItem.id);
                                    }
                                  }}
                                >
                                  {
                                    tableRelatedContactListFields.map((
                                      tableField: TableField,
                                    ): ReactElement | boolean => (
                                      <Fragment key={tableField.id}>
                                        {!!tableField.component && tableField.component(contactItem)}
                                      </Fragment>
                                    ))
                                  }
                                </TableRow>
                              ))
                            }
                          </TableBody>
                        </Table>
                      ) : (
                        <List>
                          {
                            relatedContactList.map((contactItem: Contact): ReactElement => (
                              <ListItem
                                key={contactItem.id}
                                onClick={(): void => {
                                  onContactRowClick(contactItem.id);
                                }}
                              >
                                <ListItemImage
                                  text={contactItem.fullName}
                                  link={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                                />
                                <ListItemText isBlock>
                                  {contactItem.fullName}
                                </ListItemText>
                                <ListItemText isBlock>
                                  {contactItem.title}
                                </ListItemText>
                              </ListItem>
                            ))
                          }
                        </List>
                      )
                    }
                  </div>
                </ContentListContainer>
              ) : (
                <NoContentBlock isExtended>
                  No contacts here.
                  {' '}
                  <Button
                    isText
                    isActive
                    isDefault
                    onClick={onCreateButtonClick}
                  >
                    Create New Contact
                  </Button>
                </NoContentBlock>
              )
            }
          </Fragment>
        )
      }
    </Widget>
  );
});

export default ContactWidgetStaff;
